import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import { type KeyboardEvent, forwardRef, useImperativeHandle, useRef } from 'react';
import type { ITrackingInput, TrackingInputRef } from '../../../../interfaces';
import {
  ChipDetail,
  ChipWrapper,
  ChipsContainer,
  Input,
  RemoveButton,
  TrackingInputWrapper,
} from './TrackingInput.styles';
import { useFocusManagement, useTrackingInputLogic } from './hooks';

export const TrackingInput = forwardRef<TrackingInputRef, ITrackingInput>(
  ({ chips, setChips, maxChip, onChange, isDisabled = false, placeholder }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { t } = useGlobalState();

    const { setChipRef, handleRemoveChip } = useFocusManagement({ chips, setChips, inputRef });
    const { handleChange, handlePaste, handleKeyDown, processCurrentInput } = useTrackingInputLogic({
      inputRef,
      chips,
      setChips,
      maxChip,
      isDisabled,
      onChange,
    });

    useImperativeHandle(ref, () => ({ processInput: processCurrentInput }), [processCurrentInput]);

    const IdChip = ({ trackingId, index }: { trackingId: string; index: number }) => (
      <ChipWrapper
        aria-label={`${trackingId} chip`}
        tabIndex={0}
        ref={(el) => setChipRef(el, index)}
        onKeyDown={(e) => {
          if (e.code === 'Backspace' || e.code === 'Delete') {
            handleRemoveChip(trackingId, index);
          }
        }}
      >
        <ChipDetail>
          {trackingId}
          <RemoveButton
            tabIndex={0}
            role='button'
            aria-label={`Remove ${trackingId}`}
            onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
              if (e.code === 'Enter') {
                handleRemoveChip(trackingId, index);
              }
            }}
            onClick={() => handleRemoveChip(trackingId, index)}
          />
        </ChipDetail>
      </ChipWrapper>
    );

    return (
      <TrackingInputWrapper>
        {chips.length > 0 && (
          <ChipsContainer>
            {chips?.map((id: string, index: number) => (
              <IdChip trackingId={id} key={index} index={index} />
            ))}
          </ChipsContainer>
        )}
        <Input
          ref={inputRef}
          autoComplete='off'
          placeholder={chips.length > 0 ? '' : placeholder ?? t(MasterTranslation.TrackingWidget)}
          onChange={(e) => handleChange(e)}
          onPaste={(e) => handlePaste(e)}
          onKeyDown={(e) => handleKeyDown(e)}
          data-lpignore='true'
          isChipSection={chips.length > 0}
        />
      </TrackingInputWrapper>
    );
  },
);
